import { useState } from "react";
import Hero from "../components/Hero";
import GalleryLightbox from "../components/GalleryLightbox";
import type { GallerySection } from "../api/contentful";
import {
  getGalleryLightboxSrc,
  preloadGalleryImage,
  preloadGalleryNeighbors,
  preloadGallerySection,
} from "../utils/galleryImagePreload";
import { useGallerySections } from "../hooks/useGallerySections";
import LoadingSpinner from "../components/LoadingSpinner";
import "./GalleryPage.css";

type LightboxState = {
  sectionId: string;
  mediaIndex: number;
};

const GalleryPage = () => {
  const { data: sections, loading, error } = useGallerySections();
  const [lightbox, setLightbox] = useState<LightboxState | null>(null);

  const openLightbox = (sectionId: string, mediaIndex: number) => {
    const section = sections?.find((item) => item.contentfulId === sectionId);
    if (section) {
      preloadGalleryNeighbors(section.media, mediaIndex);
      window.setTimeout(() => preloadGallerySection(section.media), 300);
    }
    setLightbox({ sectionId, mediaIndex });
  };

  const prefetchLightboxImage = (item: GallerySection["media"][number]) => {
    if (item.type === "image") {
      preloadGalleryImage(getGalleryLightboxSrc(item));
    }
  };

  const activeSection: GallerySection | undefined = lightbox
    ? sections?.find((section) => section.contentfulId === lightbox.sectionId)
    : undefined;

  const handleGridKeyDown = (
    event: React.KeyboardEvent,
    sectionId: string,
    mediaIndex: number,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openLightbox(sectionId, mediaIndex);
    }
  };

  return (
    <>
      <Hero page="gallery" titleTwo="Gallery" />

      <section className="content-container gallery-page-container">
        {loading && <LoadingSpinner className="gallery-state" />}
        {error && <p className="gallery-state">Error: {error}</p>}

        {!loading && !error && (!sections || sections.length === 0) && (
          <p className="gallery-state">No gallery sections published yet.</p>
        )}

        {!loading &&
          !error &&
          sections?.map((section) => (
            <article
              key={section.contentfulId}
              className="gallery-section"
              aria-labelledby={`gallery-section-${section.contentfulId}`}
            >
              <h2
                id={`gallery-section-${section.contentfulId}`}
                className="gallery-section-heading"
              >
                {section.title}
              </h2>

              {section.media.length > 0 ? (
                <ul className="gallery-grid">
                  {section.media.map((item, index) => (
                    <li
                      key={`${section.contentfulId}-${index}`}
                      className={`gallery-grid-item gallery-grid-item--clickable${
                        item.type === "video" ? " gallery-grid-item--video" : ""
                      }`}
                      role="button"
                      tabIndex={0}
                      aria-label={`View ${item.type} ${index + 1} in ${section.title}`}
                      onClick={() => openLightbox(section.contentfulId, index)}
                      onMouseEnter={() => prefetchLightboxImage(item)}
                      onFocus={() => prefetchLightboxImage(item)}
                      onKeyDown={(event) =>
                        handleGridKeyDown(event, section.contentfulId, index)
                      }
                    >
                      {item.type === "video" ? (
                        <video
                          src={item.url}
                          muted
                          playsInline
                          preload="metadata"
                          aria-hidden
                        />
                      ) : (
                        <img
                          src={item.url}
                          alt={`${section.title} — ${index + 1}`}
                          loading="lazy"
                        />
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="gallery-state">
                  No photos or videos in this section yet.
                </p>
              )}
            </article>
          ))}
      </section>

      {activeSection && lightbox && (
        <GalleryLightbox
          isOpen
          media={activeSection.media}
          index={lightbox.mediaIndex}
          sectionTitle={activeSection.title}
          onClose={() => setLightbox(null)}
          onIndexChange={(mediaIndex) =>
            setLightbox({ sectionId: lightbox.sectionId, mediaIndex })
          }
        />
      )}
    </>
  );
};

export default GalleryPage;
